import { Outlet, Route, Routes } from 'react-router-dom';
import { BackOfficeRoutes } from 'backoffice/routes/BackOfficeRoutes';
import { LegalDocumentRoutes } from 'legalDocument/routes/LegalDocumentRoutes';
import { AppRoutes } from 'app/routes/AppRoutes';
import {
  AUTH_ROUTES,
  AUTH_SIGN_UP_PROVIDER,
  BACK_OFFICE_ROUTES,
  DOWNLOAD_BLOCK_ACCOUNT_DATA,
  DOWNLOAD_BLOCK_CHILD_DATA,
  DOWNLOAD_CHILD_DATA,
  DOWNLOAD_MY_DATA,
  LEGAL_DOCUMENTS_ROUTES,
  LOGOUT,
  MFA_ACTION,
  NEW_ADMIN_ROUTES,
  NOT_FOUND_ROUTE,
} from 'utilities/routes';
import UnauthenticatedRoute from './UnauthenticatedRoute';
import { AuthRoutes, ContentSize } from 'auth/routes/AuthRoutes';
import NetworkRequiredRoutes from 'components/networkRequiredRoutes';
import AuthLayout from 'layout/AuthLayout';
import { NotVerifiedEmailRoute } from './NotVerifiedEmailRoute';
import RoleRequiredRoute from 'components/RoleRequiredRoute';
import { Roles } from '../firebase/interfaces';
import ErrorBoundary from '../components/tabs/ErrorBoundary';
import { ToastProvider } from '../layout/ToastProvider';
import React, { Suspense } from 'react';
import { useAutoLogout } from '../hooks/useAutoLogout';
import NewAdminRoutes from '../auth/routes/NewAdminRoutes';
import { VeradigmProvider } from '../hooks/veradigmConnectProvider/VeradigmProvider';
import { PatientProfileProvider } from '../app/my-account/pages/patient/PatientProfileContext';

const Logout = React.lazy(() => import('./Logout'));
const AuthenticatedRoute = React.lazy(() => import('./AuthenticatedRoute'));
const Action = React.lazy(() => import('auth/pages/Action/Action'));
const SignUpProvider = React.lazy(() => import('../auth/pages/SignUpProvider'));
const DownloadMyData = React.lazy(() => import('./DownloadMyData'));
const DownloadMyChildData = React.lazy(() => import('./DownloadMyChildData'));
const BlockUserData = React.lazy(() => import('./BlockUserPage'));
const BlockChildData = React.lazy(() => import('./BlockChildPage'));

const ChildDataLayout = () => (
  <NetworkRequiredRoutes>
    <ToastProvider>
      <PatientProfileProvider>
        <Outlet />
      </PatientProfileProvider>
    </ToastProvider>
  </NetworkRequiredRoutes>
);

export const AppRouter = () => {
  useAutoLogout();

  return (
    <ErrorBoundary>
      <Suspense>
        <Routes>
          <Route
            path={MFA_ACTION}
            element={
              <NetworkRequiredRoutes>
                <Action />
              </NetworkRequiredRoutes>
            }
          />

          <Route
            path={AUTH_ROUTES}
            element={
              <UnauthenticatedRoute>
                <AuthRoutes />
              </UnauthenticatedRoute>
            }
          />

          <Route
            path={NEW_ADMIN_ROUTES}
            element={
              <UnauthenticatedRoute>
                <NewAdminRoutes />
              </UnauthenticatedRoute>
            }
          />

          <Route
            path={BACK_OFFICE_ROUTES}
            element={
              <AuthenticatedRoute>
                <BackOfficeRoutes />
              </AuthenticatedRoute>
            }
          />

          <Route
            path={LEGAL_DOCUMENTS_ROUTES}
            element={
              <PatientProfileProvider>
                <LegalDocumentRoutes />
              </PatientProfileProvider>
            }
          />
          <Route
            path={DOWNLOAD_MY_DATA}
            element={
              <NetworkRequiredRoutes>
                <DownloadMyData />
              </NetworkRequiredRoutes>
            }
          />
          <Route
            path={DOWNLOAD_BLOCK_ACCOUNT_DATA}
            element={
              <NetworkRequiredRoutes>
                <BlockUserData />
              </NetworkRequiredRoutes>
            }
          />
          <Route element={<ChildDataLayout />}>
            <Route
              path={DOWNLOAD_CHILD_DATA}
              element={<DownloadMyChildData />}
            />
            <Route
              path={DOWNLOAD_BLOCK_CHILD_DATA}
              element={<BlockChildData />}
            />
          </Route>
          <Route
            path={NOT_FOUND_ROUTE}
            element={
              <VeradigmProvider>
                <AppRoutes />
              </VeradigmProvider>
            }
          />
          <Route path={LOGOUT} element={<Logout />} />

          <Route
            path={AUTH_SIGN_UP_PROVIDER}
            element={
              <NotVerifiedEmailRoute>
                <AuthLayout
                  background="bg-login-m bg-cover desktop:bg-login"
                  footerType="default"
                  contentSize={ContentSize.FULL_WIDTH}
                >
                  <NetworkRequiredRoutes>
                    <RoleRequiredRoute
                      allowedRoles={[Roles.PROVIDER]}
                      notFullProviderDataRequired
                    >
                      <ToastProvider>
                        <SignUpProvider />
                      </ToastProvider>
                    </RoleRequiredRoute>
                  </NetworkRequiredRoutes>
                </AuthLayout>
              </NotVerifiedEmailRoute>
            }
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};
